import type { CSSProperties } from "react";

/**
 * Spotlight + tooltip placement math for the Coverage Beta onboarding
 * tutorial. Forked from the classic Coverage walkthrough
 * (components/coverage/CoverageTrainingOverlay.tsx) with one deliberate
 * change: `computeTooltipStyle` takes the viewport and the tooltip's size as
 * plain parameters instead of reading `window.innerWidth`/`innerHeight` and a
 * DOM element's `offsetWidth`/`offsetHeight` itself — the classic version
 * can't be unit-tested for exactly that reason. Everything here is pure
 * math; the overlay component owns the DOM reads that feed it.
 */

/**
 * The subset of DOMRect this module needs. A real DOMRect satisfies this
 * structurally, so callers can pass `el.getBoundingClientRect()` directly;
 * tests can pass a plain object instead of constructing one (useful since
 * jsdom has no real layout engine and always reports zero-size rects).
 */
export interface Rect {
  top: number;
  left: number;
  right: number;
  bottom: number;
  width: number;
  height: number;
}

export interface Viewport {
  width: number;
  height: number;
}

export interface Size {
  width: number;
  height: number;
}

export type TutorialPlacement = "top" | "bottom" | "left" | "right" | "center";

export const TOOLTIP_MAX_WIDTH = 360;
export const SPOTLIGHT_PADDING = 8;
export const TOOLTIP_GAP = 16;
export const VIEWPORT_MARGIN = 12;
/**
 * Fallback tooltip height before it has been measured — only used on the
 * very first render of a step, before the overlay's `useLayoutEffect`
 * re-measures against the real, mounted element (mirrors the width
 * fallback, `TOOLTIP_MAX_WIDTH`).
 */
const DEFAULT_TOOLTIP_HEIGHT = 200;

/**
 * A `clip-path` polygon that dims the whole viewport except a padded cutout
 * over `rect`. Because `clip-path` clips hit-testing along with paint,
 * clicks inside the cutout reach the spotlit element for free through the
 * scrim div — the scrim still blocks everything outside the hole.
 */
export function getSpotlightClipPath(
  rect: Rect,
  padding: number = SPOTLIGHT_PADDING,
): string {
  const top = rect.top - padding;
  const left = rect.left - padding;
  const right = rect.right + padding;
  const bottom = rect.bottom + padding;
  return `polygon(
    0% 0%, 100% 0%, 100% 100%, 0% 100%, 0% 0%,
    ${left}px ${top}px, ${left}px ${bottom}px, ${right}px ${bottom}px, ${right}px ${top}px, ${left}px ${top}px
  )`;
}

/**
 * Where to fix-position the tooltip card for a given placement, clamped so
 * it never runs off-screen. `placement: "center"` (or a null `rect` — no
 * anchor resolved) ignores `rect` entirely and centers the card over the
 * dimmed scrim instead of pointing at anything.
 */
export function computeTooltipStyle(
  placement: TutorialPlacement,
  rect: Rect | null,
  tooltipSize: Size,
  viewport: Viewport,
): CSSProperties {
  if (placement === "center" || !rect) {
    return {
      position: "fixed",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      maxWidth: TOOLTIP_MAX_WIDTH,
    };
  }

  const pad = SPOTLIGHT_PADDING;
  const gap = TOOLTIP_GAP;
  const tw = tooltipSize.width || TOOLTIP_MAX_WIDTH;
  const th = tooltipSize.height || DEFAULT_TOOLTIP_HEIGHT;
  const m = VIEWPORT_MARGIN;

  let top: number;
  let left: number;

  switch (placement) {
    case "right": {
      left = rect.right + pad + gap;
      top = rect.top + rect.height / 2 - th / 2;
      break;
    }
    case "left": {
      left = rect.left - pad - gap - tw;
      top = rect.top + rect.height / 2 - th / 2;
      break;
    }
    case "bottom": {
      top = rect.bottom + pad + gap;
      left = rect.left + rect.width / 2 - tw / 2;
      break;
    }
    case "top": {
      top = rect.top - pad - gap - th;
      left = rect.left + rect.width / 2 - tw / 2;
      break;
    }
  }

  // Clamp within the viewport.
  top = Math.max(m, Math.min(top, viewport.height - th - m));
  left = Math.max(m, Math.min(left, viewport.width - tw - m));

  return {
    position: "fixed",
    maxWidth: TOOLTIP_MAX_WIDTH,
    top,
    left,
  };
}
